"use client"

import { useCallback, useEffect, useState, useMemo } from "react"
import { getHome } from "@/features/homeowners/api/homeowners.api"
import {
    updateWorkScopeStatus,
    getAllWorkOrders,
} from "@/features/work-orders/api/work-orders.api"
import { useSession } from "@/features/auth/context/session.context"
import { authFetchJson } from "@/shared/lib/auth-fetch"
import { UserRole } from "@/shared/constants/user-roles"
import { Home } from "@/features/homeowners/types/api-response/home-api-response.type"
import { Partner } from "@/features/partners/types/partner.type"
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/shared/components/ui/dialog"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/shared/components/ui/select"
import { Button } from "@/shared/components/ui/button"
import { Label } from "@/shared/components/ui/label"
import {
    ReactFlow,
    Background,
    Controls,
    Handle,
    Position,
    useReactFlow,
} from "@xyflow/react"
import "@xyflow/react/dist/style.css"
import { mockHomeData } from "./workflow-mock-data"

// Helper function to create a Home object from mock data
const createMockHomeFromData = (mockData: any, homeId: number): Home => {
    return {
        id: homeId,
        address: "123 Test Street, Test City, TX 12345",
        city: "Test City",
        state: "TX",
        county: "Test County",
        zipCode: "12345",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        currentStep: 1,
        isReported: true,
        projectGeneralNotes: null,
        dogsInHome: null,
        latitude: null,
        longitude: null,
        shedNeeded: null,
        hasFloodInsurance: null,
        hasHomeInsurance: null,

        // Mock homeowner data
        homeowner: {
            id: homeId,
            county: "Test County",
            primaryLanguage: "English",
            secondaryContact: "",
            secondaryPhone: "",
            homeownerAge: "",
            residentType: "",
            medicalNeeds: "",
            numChildrenUnder18: 0,
            isFirstResponder: false,
            isSingleParent: false,
            isVeteran: false,
            appliedFEMA: false,
            femaStatus: "",
            appliedSBA: false,
            sbaStatus: "",
            shedNeeded: null,
            safetyConcerns: "",
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            user: {
                id: 1,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                fullname: "Test Homeowner",
                email: "test@example.com",
                email_verified: true,
                organizationName: null,
                age: null,
                servingPreference: null,
                groupNotes: null,
                provider: "email",
                profilePicture: null,
                availability: null,
                dateRange: null,
                experienceConstruction: null,
                experienceDataManagement: null,
                experienceStorytelling: null,
                mealPreparationAbility: null,
                experienceLeadingTeams: null,
                password: null,
                skillsAndQualities: null,
                otherLanguages: null,
                hasServedBefore: null,
                isChristFollower: null,
                preferredLocation: null,
                currentStep: 1,
                isComplete: false, // User field - not Home field
                resetPasswordToken: null,
                resetPasswordExpires: null,
                otp: null,
                otpExpires: null,
                hoursWorked: 0,
                daysServed: 0,
                trainingVideosWatched: 0,
                workScopesCompleted: 0,
                selectedRegion: null,
                expectations: null,
                stmObjectives: null,
                devotions: null,
                serveFeedback: null,
                chatNotifications: true,
                workOrderNotifications: true,
                phone: "",
                phone_verified: false,
                isTwoFactorEnabled: false,
                twoFactorCode: null,
                twoFactorCodeExpires: null,
                googleAccountLinked: null,
            },
            home: null,
        },

        // Repairability Info - contains most of the fields used in the flowchart
        repairabilityInfo: {
            id: 1,
            assessmentCompleted: mockData.assessmentCompleted,
            assessmentCompletedClaimedBy: mockData.assessmentCompletedClaimedBy,
            assessorName: "Test Assessor",
            residenceType: "Single Family",
            structureType: "Wood Frame",
            ownerNameIfRental: "",
            ownerPhoneNumberIfRental: "",
            residentType: null,
            canBeRepaired: "Yes",
            newHousingHelpNeeded: "No",
            rebuildingHelpNeeded: "Yes",
            permissionToWork: true,
            rebuildingSuppliesHelpNeeded: null,
            heightOfWater: "2 feet",
            totalSquareFeet: 1500,
            buildingHeight: "1 story",
            affectedSquareFeet: 1500,
            potentialStructuralProblems: "None",
            structuralProblemDescription: "",
            // Map mock data fields to repairabilityInfo
            roofTarpNeeded: mockData.roofTarpNeeded,
            roofTarpNeededClaimedBy: mockData.roofTarpNeededClaimedBy,
        } as any,

        // Other required info objects with minimal data
        treeDebrisInfo: {
            id: 1,
            treeWorkNeeded: mockData.treeWorkNeeded,
            treeWorkNeededClaimedBy: mockData.treeWorkNeededClaimedBy,
            exteriorDebrisRemovalNeeded: mockData.exteriorDebrisRemovalNeeded,
            exteriorDebrisRemovalNeededClaimedBy:
                mockData.exteriorDebrisRemovalNeededClaimedBy,
            heavyEquipmentNeeded: mockData.heavyEquipmentNeeded,
            heavyEquipmentNeededClaimedBy:
                mockData.heavyEquipmentNeededClaimedBy,
        },
        crawlSpaceInfo: {
            id: 1,
            crawlSpaceMuck: mockData.crawlSpaceMuck,
            crawlSpaceMuckClaimedBy: mockData.crawlSpaceMuckClaimedBy,
            crawlSpaceInsulation: mockData.crawlSpaceInsulation,
            crawlSpaceInsulationClaimedBy:
                mockData.crawlSpaceInsulationClaimedBy,
        },
        miscellaneousInfo: {
            id: 1,
            drivewayRepair: mockData.drivewayRepair,
            drivewayRepairClaimedBy: mockData.drivewayRepairClaimedBy,
            miscellaneous: mockData.miscellaneous,
            miscellaneousClaimedBy: mockData.miscellaneousClaimedBy,
        },
        permitInspectionStatusInfo: {
            id: 1,
            roofingInspections503: mockData.roofingInspections503,
            roofingInspections503ClaimedBy:
                mockData.roofingInspections503ClaimedBy,
            framingInspections105: mockData.framingInspections105,
            framingInspections105ClaimedBy:
                mockData.framingInspections105ClaimedBy,
            electricalInspections304: mockData.electricalInspections304,
            electricalInspections304ClaimedBy:
                mockData.electricalInspections304ClaimedBy,
            electricalInspections305: mockData.electricalInspections305,
            electricalInspections305ClaimedBy:
                mockData.electricalInspections305ClaimedBy,
            hvacInspections401: mockData.hvacInspections401,
            hvacInspections401ClaimedBy: mockData.hvacInspections401ClaimedBy,
            plumbingInspections202: mockData.plumbingInspections202,
            plumbingInspections202ClaimedBy:
                mockData.plumbingInspections202ClaimedBy,
            insulationInspections107: mockData.insulationInspections107,
            insulationInspections107ClaimedBy:
                mockData.insulationInspections107ClaimedBy,
            drywallInspections: mockData.drywallInspections,
            drywallInspectionsClaimedBy: mockData.drywallInspectionsClaimedBy,
            plumbingInspections204: mockData.plumbingInspections204,
            plumbingInspections204ClaimedBy:
                mockData.plumbingInspections204ClaimedBy,
            hvacInspections402: mockData.hvacInspections402,
            hvacInspections402ClaimedBy: mockData.hvacInspections402ClaimedBy,
            finalInspection106: mockData.finalInspection106,
            finalInspection106ClaimedBy: mockData.finalInspection106ClaimedBy,
        },
        muckGutInfo: {
            id: 1,
            muckGutNeeded: mockData.muckGutNeeded,
            muckGutNeededClaimedBy: mockData.muckGutNeededClaimedBy,
            heightOfWallRemoval: 2,
            ceilingRemoved: true,
            description: "Test",
        },
        moldRemediationInfo: {
            id: 1,
            moldRemediation: mockData.moldRemediation,
            moldRemediationClaimedBy: mockData.moldRemediationClaimedBy,
            highestMoistureLevel: 15,
            description: "Test",
        },
        roofingInfo: {
            id: 1,
            roofing: mockData.roofing,
            roofingClaimedBy: mockData.roofingClaimedBy,
            kindOfRoofWork: "Repair",
            roofTarpNeeded: mockData.roofTarpNeeded,
            roofPitch: "4:12",
        },
        rebuildPrepInfo: {
            id: 1,
            rebuildPrep: mockData.rebuildPrep,
            rebuildPrepClaimedBy: mockData.rebuildPrepClaimedBy,
            needsWallsRepaired: "yes",
            needsSubfloorRepair: "no",
            needsWindows: "yes",
            numberOfWindows: 8,
        },
        roughCarpentryInfo: {
            id: 1,
            roughCarpentry: mockData.roughCarpentry,
            roughCarpentryClaimedBy: mockData.roughCarpentryClaimedBy,
            description: "Test",
        },
        hvacInfo: {
            id: 1,
            hvac: mockData.hvac,
            hvacClaimedBy: mockData.hvacClaimedBy,
            mechanicalRoughIn: mockData.mechanicalRoughIn,
            mechanicalRoughInClaimedBy: mockData.mechanicalRoughInClaimedBy,
            newUnitRequired: false,
            description: "Test",
        },
        electricalInfo: {
            id: 1,
            electrical: mockData.electrical,
            electricalClaimedBy: mockData.electricalClaimedBy,
            electricalRoughIn: mockData.electricalRoughIn,
            electricalRoughInClaimedBy: mockData.electricalRoughInClaimedBy,
            description: "Test",
        },
        plumbingInfo: {
            id: 1,
            plumbing: mockData.plumbing,
            plumbingClaimedBy: mockData.plumbingClaimedBy,
            plumbingRoughIn: mockData.plumbingRoughIn,
            plumbingRoughInClaimedBy: mockData.plumbingRoughInClaimedBy,
            tubSetRequired: true,
            plumbingDescription: "Test",
            bathroomVanities: "Standard",
            bathroomRebuildDetails: "Test",
        },
        insulationInstallInfo: {
            id: 1,
            insulationInstall: mockData.insulationInstall,
            insulationInstallClaimedBy: mockData.insulationInstallClaimedBy,
            blanketWallInsulation: "R-13",
            rigidWallInsulation: "R-5",
            ceilingInsulation: "R-30",
            description: "Test",
        },
        drywallHangingInfo: {
            id: 1,
            drywallHanging: mockData.drywallHanging,
            drywallHangingClaimedBy: mockData.drywallHangingClaimedBy,
            interiorWallMaterial: ["Drywall"],
            totalSquareFeet: 1200,
            hasTexturing: "yes",
            description: "Test",
        },
        mudAndTapeInfo: {
            id: 1,
            mudAndTape: mockData.mudAndTape,
            mudAndTapeClaimedBy: mockData.mudAndTapeClaimedBy,
            description: "Test",
        },
        interiorPaintInfo: {
            id: 1,
            interiorPaint: mockData.interiorPaint,
            interiorPaintClaimedBy: mockData.interiorPaintClaimedBy,
            wallPaintSquareFeet: 800,
            ceilingPaintSquareFeet: 400,
        },
        cabinetInstallInfo: {
            id: 1,
            cabinetInstall: mockData.cabinetInstall,
            cabinetInstallClaimedBy: mockData.cabinetInstallClaimedBy,
            counterInstall: mockData.counterInstall,
            counterInstallClaimedBy: mockData.counterInstallClaimedBy,
            applianceInstall: mockData.applianceInstall,
            applianceInstallClaimedBy: mockData.applianceInstallClaimedBy,
            kitchenCabinetLowers: 12,
            kitchenCabinetUppers: 8,
            description: "Test",
        },
        flooringInstallInfo: {
            id: 1,
            flooringInstall: mockData.flooringInstall,
            flooringInstallClaimedBy: mockData.flooringInstallClaimedBy,
            subfloorType: "Plywood",
            flooringSquareFeet: 1200,
            description: "Test",
        },
        installInteriorDoorsInfo: {
            id: 1,
            installInteriorDoors: mockData.installInteriorDoors,
            installInteriorDoorsClaimedBy:
                mockData.installInteriorDoorsClaimedBy,
            doorsToReplace: 6,
            doorsNeedTrim: 4,
            linearFeetOfCasings: 48,
        },
        exteriorDoorInstallInfo: {
            id: 1,
            exteriorDoorInstall: mockData.exteriorDoorInstall,
            exteriorDoorInstallClaimedBy: mockData.exteriorDoorInstallClaimedBy,
            doorsReplacing: 2,
            doorsNeedTrim: 2,
        },
        trimInstallInfo: {
            id: 1,
            trimInstall: mockData.trimInstall,
            claimedBy: mockData.trimInstallClaimedBy,
            linearFeetOfBaseboard: 120,
            linearFeetOfWindowCasings: 48,
        },
        bathroomRebuildInfo: {
            id: 1,
            bathroomRebuild: mockData.bathroomRebuild,
            bathroomRebuildClaimedBy: mockData.bathroomRebuildClaimedBy,
            description: "Test",
        },
        homeStatusInfo: {
            id: 1,
            leadPaint: "yes",
            permissionToWork: true,
            mediaWaiverSigned: true,
            workWaiverSigned: true,
            helpWithinScope: true,
            functionallyComplete: false,
            fullyComplete: false,
        },
    } as any
}

type WorkflowFlowchartProps = {
    homeId: number
}

// Define the new status options for the dropdown
type StatusOption =
    | "Unknown"
    | "No Assistance Required"
    | "Not Ready for Work"
    | "Needed Not Started"
    | "Reassessment Needed"
    | "25%"
    | "50%"
    | "75%"
    | "Ready for Inspection"
    | "Complete"
    | "Not Complete"

// Define organization options
type OrganizationOption = "Homeowner" | string

const isHomeownerClaimedBy = (claimedBy?: string): boolean =>
    claimedBy === "Homeowner" || claimedBy === "Homeowner Scopes"

const getStatusColor = (status: any, claimedBy?: string): string => {
    // Handle null or undefined values

    // Special rules for progress statuses based on claimedBy
    if (
        [
            "Needed Not Started",
            "Reassessment Needed",
            "25%",
            "50%",
            "75%",
            "Ready for Inspection",
            "",
            null,
            undefined,
        ].includes(status)
    ) {
        if (claimedBy === "AR Scopes") {
            return "bg-blue-950"
        } else if (isHomeownerClaimedBy(claimedBy)) {
            return "bg-fuchsia-700"
        } else if (claimedBy === "Partner Scopes") {
            return "bg-violet-700"
        }
    }

    switch (status) {
        case "Unknown":
            return "bg-yellow-600"
        case "No Assistance Required":
            return "bg-gray-300"
        case "Not Ready for Work":
            return "bg-neutral-700"
        case "Complete":
            return "bg-emerald-700"
        case "Not Complete": // This is not selectable status, this status for functionlaity complete & fully complete fields to make it as non-completed
            return "bg-yellow-200"
        default: // has value but not from the list
            return ""
    }
}

// Database field mapping for each node
const nodeFieldMapping: Record<
    string,
    { field: string; type: "character varying" }
> = {
    "Assess Needs": { field: "assessmentCompleted", type: "character varying" },
    "M&G Crawl Space": { field: "crawlSpaceMuck", type: "character varying" },
    "Muck and Gut Interior": {
        field: "muckGutNeeded",
        type: "character varying",
    },
    "Tarp Roof": { field: "roofTarpNeeded", type: "character varying" },
    "Tree Work": { field: "treeWorkNeeded", type: "character varying" },
    "Insulate Crawl Space": {
        field: "crawlSpaceInsulation",
        type: "character varying",
    },
    "Mold Remediation": { field: "moldRemediation", type: "character varying" },
    "Install Roofing": { field: "roofing", type: "character varying" },
    "Repair Driveway": { field: "drivewayRepair", type: "character varying" },
    Misc: { field: "miscellaneous", type: "character varying" },
    "Rebuild Prep": { field: "rebuildPrep", type: "character varying" },
    "Roof Final Inspection": {
        field: "roofingInspections501",
        type: "character varying",
    },
    "Rough Carpentry": { field: "roughCarpentry", type: "character varying" },
    "Rough Electrical": {
        field: "electricalRoughIn",
        type: "character varying",
    },
    "Rough Mechanical": {
        field: "mechanicalRoughIn",
        type: "character varying",
    },
    "Rough Plumbing": { field: "plumbingRoughIn", type: "character varying" },
    "Rough Carpentry Inspection": {
        field: "framingInspections105",
        type: "character varying",
    },
    "Rough Mechanical Inspection": {
        field: "hvacInspections401",
        type: "character varying",
    },
    Insulation: { field: "insulationInstall", type: "character varying" },
    "Insulation Inspection": {
        field: "insulationInspections107",
        type: "character varying",
    },
    "Drywall Hanging": { field: "drywallHanging", type: "character varying" },
    "Drywall Inspection": {
        field: "drywallInspections",
        type: "character varying",
    },
    "Mud and Tape": { field: "mudAndTape", type: "character varying" },
    "Interior Paint": { field: "interiorPaint", type: "character varying" },
    "Install Cabinets": { field: "cabinetInstall", type: "character varying" },
    "HVAC Final": { field: "hvac", type: "character varying" },
    "Bathroom Rebuild": { field: "bathroomRebuild", type: "character varying" },
    "Install Counters": { field: "counterInstall", type: "character varying" },
    "Electrical Fixtures": { field: "electrical", type: "character varying" },
    "Plumbing Fixtures": { field: "plumbing", type: "character varying" },
    "Final Electrical Inspection": {
        field: "electricalInspections304",
        type: "character varying",
    },
    "Final Plumbing Inspection": {
        field: "plumbingInspections202",
        type: "character varying",
    },
    "Final HVAC Inspection": {
        field: "hvacInspections401",
        type: "character varying",
    },
    "Final Inspection": {
        field: "finalInspection106",
        type: "character varying",
    },
    "Flooring Install": { field: "flooringInstall", type: "character varying" },
    "Install Interior Doors": {
        field: "installInteriorDoors",
        type: "character varying",
    },
    "Install Exterior Doors": {
        field: "exteriorDoorInstall",
        type: "character varying",
    },
    "Trim Install": { field: "trimInstall", type: "character varying" },
    "Heavy Equipment / Demo": {
        field: "heavyEquipmentNeeded",
        type: "character varying",
    },
    "Exterior Debris Removal": {
        field: "exteriorDebrisRemovalNeeded",
        type: "character varying",
    },
    "Install Appliances": {
        field: "applianceInstall",
        type: "character varying",
    },
}

const determineWorkStatus = (homeData: Home, nodeLabel: string): string => {
    const fieldMapping = nodeFieldMapping[nodeLabel]

    if (!fieldMapping) {
        return "Unknown"
    }

    const { field, type } = fieldMapping

    // Navigate through the homeData object to find the field
    // The field might be nested in different objects
    let value: any = null

    // Check in repairabilityInfo
    if (
        homeData.repairabilityInfo &&
        homeData.repairabilityInfo[
            field as keyof typeof homeData.repairabilityInfo
        ] !== undefined
    ) {
        value =
            homeData.repairabilityInfo[
                field as keyof typeof homeData.repairabilityInfo
            ]
    }
    // Check in treeDebrisInfo
    else if (
        homeData.treeDebrisInfo &&
        homeData.treeDebrisInfo[
            field as keyof typeof homeData.treeDebrisInfo
        ] !== undefined
    ) {
        value =
            homeData.treeDebrisInfo[
                field as keyof typeof homeData.treeDebrisInfo
            ]
    }
    // Check in crawlSpaceInfo
    else if (
        (homeData as any).crawlSpaceInfo &&
        (homeData as any).crawlSpaceInfo[field] !== undefined
    ) {
        value = (homeData as any).crawlSpaceInfo[field]
    }
    // Check in miscellaneousInfo
    else if (
        (homeData as any).miscellaneousInfo &&
        (homeData as any).miscellaneousInfo[field] !== undefined
    ) {
        value = (homeData as any).miscellaneousInfo[field]
    }
    // Check in permitInspectionStatusInfo
    else if (
        (homeData as any).permitInspectionStatusInfo &&
        (homeData as any).permitInspectionStatusInfo[field] !== undefined
    ) {
        value = (homeData as any).permitInspectionStatusInfo[field]
    }
    // Check in muckGutInfo
    else if (
        homeData.muckGutInfo &&
        homeData.muckGutInfo[field as keyof typeof homeData.muckGutInfo] !==
            undefined
    ) {
        value = homeData.muckGutInfo[field as keyof typeof homeData.muckGutInfo]
    }
    // Check in moldRemediationInfo
    else if (
        homeData.moldRemediationInfo &&
        homeData.moldRemediationInfo[
            field as keyof typeof homeData.moldRemediationInfo
        ] !== undefined
    ) {
        value =
            homeData.moldRemediationInfo[
                field as keyof typeof homeData.moldRemediationInfo
            ]
    }
    // Check in roofingInfo
    else if (
        homeData.roofingInfo &&
        homeData.roofingInfo[field as keyof typeof homeData.roofingInfo] !==
            undefined
    ) {
        value = homeData.roofingInfo[field as keyof typeof homeData.roofingInfo]
    }
    // Check in rebuildPrepInfo
    else if (
        homeData.rebuildPrepInfo &&
        homeData.rebuildPrepInfo[
            field as keyof typeof homeData.rebuildPrepInfo
        ] !== undefined
    ) {
        value =
            homeData.rebuildPrepInfo[
                field as keyof typeof homeData.rebuildPrepInfo
            ]
    }
    // Check in roughCarpentryInfo
    else if (
        homeData.roughCarpentryInfo &&
        homeData.roughCarpentryInfo[
            field as keyof typeof homeData.roughCarpentryInfo
        ] !== undefined
    ) {
        value =
            homeData.roughCarpentryInfo[
                field as keyof typeof homeData.roughCarpentryInfo
            ]
    }
    // Check in hvacInfo
    else if (
        homeData.hvacInfo &&
        homeData.hvacInfo[field as keyof typeof homeData.hvacInfo] !== undefined
    ) {
        value = homeData.hvacInfo[field as keyof typeof homeData.hvacInfo]
    }
    // Check in electricalInfo
    else if (
        homeData.electricalInfo &&
        homeData.electricalInfo[
            field as keyof typeof homeData.electricalInfo
        ] !== undefined
    ) {
        value =
            homeData.electricalInfo[
                field as keyof typeof homeData.electricalInfo
            ]
    }
    // Check in plumbingInfo
    else if (
        homeData.plumbingInfo &&
        homeData.plumbingInfo[field as keyof typeof homeData.plumbingInfo] !==
            undefined
    ) {
        value =
            homeData.plumbingInfo[field as keyof typeof homeData.plumbingInfo]
    }
    // Check in insulationInstallInfo
    else if (
        homeData.insulationInstallInfo &&
        homeData.insulationInstallInfo[
            field as keyof typeof homeData.insulationInstallInfo
        ] !== undefined
    ) {
        value =
            homeData.insulationInstallInfo[
                field as keyof typeof homeData.insulationInstallInfo
            ]
    }
    // Check in drywallHangingInfo
    else if (
        homeData.drywallHangingInfo &&
        homeData.drywallHangingInfo[
            field as keyof typeof homeData.drywallHangingInfo
        ] !== undefined
    ) {
        value =
            homeData.drywallHangingInfo[
                field as keyof typeof homeData.drywallHangingInfo
            ]
    }
    // Check in mudAndTapeInfo
    else if (
        homeData.mudAndTapeInfo &&
        homeData.mudAndTapeInfo[
            field as keyof typeof homeData.mudAndTapeInfo
        ] !== undefined
    ) {
        value =
            homeData.mudAndTapeInfo[
                field as keyof typeof homeData.mudAndTapeInfo
            ]
    }
    // Check in interiorPaintInfo
    else if (
        homeData.interiorPaintInfo &&
        homeData.interiorPaintInfo[
            field as keyof typeof homeData.interiorPaintInfo
        ] !== undefined
    ) {
        value =
            homeData.interiorPaintInfo[
                field as keyof typeof homeData.interiorPaintInfo
            ]
    }
    // Check in cabinetInstallInfo
    else if (
        homeData.cabinetInstallInfo &&
        homeData.cabinetInstallInfo[
            field as keyof typeof homeData.cabinetInstallInfo
        ] !== undefined
    ) {
        value =
            homeData.cabinetInstallInfo[
                field as keyof typeof homeData.cabinetInstallInfo
            ]
    }
    // Check in flooringInstallInfo
    else if (
        homeData.flooringInstallInfo &&
        homeData.flooringInstallInfo[
            field as keyof typeof homeData.flooringInstallInfo
        ] !== undefined
    ) {
        value =
            homeData.flooringInstallInfo[
                field as keyof typeof homeData.flooringInstallInfo
            ]
    }
    // Check in installInteriorDoorsInfo
    else if (
        homeData.installInteriorDoorsInfo &&
        homeData.installInteriorDoorsInfo[
            field as keyof typeof homeData.installInteriorDoorsInfo
        ] !== undefined
    ) {
        value =
            homeData.installInteriorDoorsInfo[
                field as keyof typeof homeData.installInteriorDoorsInfo
            ]
    }
    // Check in exteriorDoorInstallInfo
    else if (
        homeData.exteriorDoorInstallInfo &&
        homeData.exteriorDoorInstallInfo[
            field as keyof typeof homeData.exteriorDoorInstallInfo
        ] !== undefined
    ) {
        value =
            homeData.exteriorDoorInstallInfo[
                field as keyof typeof homeData.exteriorDoorInstallInfo
            ]
    }
    // Check in trimInstallInfo
    else if (
        homeData.trimInstallInfo &&
        homeData.trimInstallInfo[
            field as keyof typeof homeData.trimInstallInfo
        ] !== undefined
    ) {
        value =
            homeData.trimInstallInfo[
                field as keyof typeof homeData.trimInstallInfo
            ]
    }
    // Check in bathroomRebuildInfo
    else if (
        (homeData as any).bathroomRebuildInfo &&
        (homeData as any).bathroomRebuildInfo[field] !== undefined
    ) {
        value = (homeData as any).bathroomRebuildInfo[field]
    }
    // Check in homeStatusInfo
    else if (
        homeData.homeStatusInfo &&
        homeData.homeStatusInfo[
            field as keyof typeof homeData.homeStatusInfo
        ] !== undefined
    ) {
        value =
            homeData.homeStatusInfo[
                field as keyof typeof homeData.homeStatusInfo
            ]
    }

    // Return the value as is (it could be null, undefined, or a string)
    return value
}

const determineClaimedBy = (homeData: Home, nodeLabel: string): string => {
    // Inspections are homeowner-owned work.
    const inspectionNodes = [
        "Roof Final Inspection",
        "Rough Carpentry Inspection", 
        "Rough Electrical Inspection",
        "Rough Mechanical Inspection",
        "Rough Plumbing / Tub Set Inspection",
        "Insulation Inspection",
        "Drywall Inspection",
        "Final Electrical Inspection",
        "Final Plumbing Inspection", 
        "Final HVAC Inspection",
        "Final Inspection"
    ]
    
    if (inspectionNodes.includes(nodeLabel)) {
        return "Homeowner"
    }

    const fieldMapping = nodeFieldMapping[nodeLabel]

    if (!fieldMapping) {
        return ""
    }

    const { field, type } = fieldMapping
    // Special cases for field name mismatches
    let claimedByField: string
    if (nodeLabel === "Tarp Roof") {
        claimedByField = "roofTarpClaimedBy"
    } else if (nodeLabel === "Tree Work") {
        claimedByField = "treeClaimedBy"
    } else if (nodeLabel === "Heavy Equipment / Demo") {
        claimedByField = "heavyClaimedBy"
    } else if (nodeLabel === "Exterior Debris Removal") {
        claimedByField = "debrisClaimedBy"
    } else if (nodeLabel === "Repair Driveway") {
        claimedByField = "drivewayClaimedBy"
    } else if (nodeLabel === "Install Cabinets") {
        claimedByField = "cabinetClaimedBy"
    } else if (nodeLabel === "Install Counters") {
        claimedByField = "counterClaimedBy"
    } else if (nodeLabel === "Install Appliances") {
        claimedByField = "applianceClaimedBy"
    } else if (nodeLabel === "Flooring Install") {
        claimedByField = "claimedBy"
    } else if (nodeLabel === "Install Interior Doors") {
        claimedByField = "claimedBy"
    } else if (nodeLabel === "HVAC Final") {
        claimedByField = "claimedBy"
    } else if (nodeLabel === "Rough Carpentry") {
        claimedByField = "claimedBy"
    } else if (nodeLabel === "Insulation") {
        claimedByField = "claimedBy"
    } else if (nodeLabel === "Drywall Hanging") {
        claimedByField = "claimedBy"
    } else if (nodeLabel === "Mud and Tape") {
        claimedByField = "claimedBy"
    } else if (nodeLabel === "Interior Paint") {
        claimedByField = "claimedBy"
    } else if (nodeLabel === "Rebuild Prep") {
        claimedByField = "claimedBy"
    } else if (nodeLabel === "Misc") {
        claimedByField = "claimedBy"
    } else if (nodeLabel === "Roof Final Inspection") {
        claimedByField = "roofingInspections503"
    } else if (nodeLabel === "Final Plumbing Inspection") {
        claimedByField = "plumbingInspections204"
    } else if (nodeLabel === "Mold Remediation") {
        claimedByField = "claimedBy"
    } else {
        claimedByField = `${field}ClaimedBy`
    }

    // Navigate through the homeData object to find the claimedBy field
    // The field might be nested in different objects
    let value: any = null

    // Special cases for nodes with generic "claimedBy" field - check specific info objects first
    if (nodeLabel === "Mold Remediation" && homeData.moldRemediationInfo) {
        value = (homeData.moldRemediationInfo as any).claimedBy
    } else if (nodeLabel === "Rebuild Prep" && homeData.rebuildPrepInfo) {
        value = (homeData.rebuildPrepInfo as any).claimedBy
    } else if (nodeLabel === "Rough Carpentry" && homeData.roughCarpentryInfo) {
        value = (homeData.roughCarpentryInfo as any).claimedBy
    } else if (nodeLabel === "Insulation" && homeData.insulationInstallInfo) {
        value = (homeData.insulationInstallInfo as any).claimedBy
    } else if (nodeLabel === "Muck and Gut Interior" && homeData.muckGutInfo) {
        value = (homeData.muckGutInfo as any).claimedBy
    } else if (nodeLabel === "Drywall Hanging" && homeData.drywallHangingInfo) {
        value = (homeData.drywallHangingInfo as any).claimedBy
    } else if (nodeLabel === "Mud and Tape" && homeData.mudAndTapeInfo) {
        value = (homeData.mudAndTapeInfo as any).claimedBy
    } else if (nodeLabel === "Interior Paint" && homeData.interiorPaintInfo) {
        value = (homeData.interiorPaintInfo as any).claimedBy
    } else if (nodeLabel === "Rough Electrical" && homeData.electricalInfo) {
        value = (homeData.electricalInfo as any).electricalRoughInClaimedBy
    } else if (nodeLabel === "Rough Mechanical" && homeData.hvacInfo) {
        value = (homeData.hvacInfo as any).mechanicalRoughInClaimedBy
    } else if (nodeLabel === "Rough Plumbing" && homeData.plumbingInfo) {
        value = (homeData.plumbingInfo as any).plumbingRoughInClaimedBy
    } else if (nodeLabel === "HVAC Final" && homeData.hvacInfo) {
        value = (homeData.hvacInfo as any).claimedBy
    } else if (nodeLabel === "Electrical Fixtures" && homeData.electricalInfo) {
        value = (homeData.electricalInfo as any).claimedBy
    } else if (nodeLabel === "Plumbing Fixtures" && homeData.plumbingInfo) {
        value = (homeData.plumbingInfo as any).claimedBy
    } else if (
        nodeLabel === "Flooring Install" &&
        homeData.flooringInstallInfo
    ) {
        value = (homeData.flooringInstallInfo as any).claimedBy
    } else if (
        nodeLabel === "Install Interior Doors" &&
        homeData.installInteriorDoorsInfo
    ) {
        value = (homeData.installInteriorDoorsInfo as any).claimedBy
    } else if (
        nodeLabel === "Install Exterior Doors" &&
        homeData.exteriorDoorInstallInfo
    ) {
        value = (homeData.exteriorDoorInstallInfo as any).claimedBy
    } else if (
        nodeLabel === "Final HVAC Inspection" &&
        (homeData as any).permitInspectionStatusInfo
    ) {
        value = ((homeData as any).permitInspectionStatusInfo as any)
            .hvacInspections402
    } else if (
        nodeLabel === "Final Plumbing Inspection" &&
        (homeData as any).permitInspectionStatusInfo
    ) {
        value = ((homeData as any).permitInspectionStatusInfo as any)
            .plumbingInspections204
    } else if (
        nodeLabel === "Final Electrical Inspection" &&
        (homeData as any).permitInspectionStatusInfo
    ) {
        value = ((homeData as any).permitInspectionStatusInfo as any)
            .electricalInspections305
    } else if (
        nodeLabel === "Roof Final Inspection" &&
        (homeData as any).permitInspectionStatusInfo
    ) {
        value = ((homeData as any).permitInspectionStatusInfo as any)
            .roofingInspections503
    } else if (nodeLabel === "Trim Install" && homeData.trimInstallInfo) {
        value = (homeData.trimInstallInfo as any).claimedBy
    } else if (
        nodeLabel === "Bathroom Rebuild" &&
        (homeData as any).bathroomRebuildInfo
    ) {
        value = ((homeData as any).bathroomRebuildInfo as any).claimedBy
    }
    // Check in repairabilityInfo
    else if (
        homeData.repairabilityInfo &&
        homeData.repairabilityInfo[
            claimedByField as keyof typeof homeData.repairabilityInfo
        ] !== undefined
    ) {
        value =
            homeData.repairabilityInfo[
                claimedByField as keyof typeof homeData.repairabilityInfo
            ]
    }
    // Check in treeDebrisInfo
    else if (
        homeData.treeDebrisInfo &&
        homeData.treeDebrisInfo[
            claimedByField as keyof typeof homeData.treeDebrisInfo
        ] !== undefined
    ) {
        value =
            homeData.treeDebrisInfo[
                claimedByField as keyof typeof homeData.treeDebrisInfo
            ]
    }
    // Check in crawlSpaceInfo
    else if (
        (homeData as any).crawlSpaceInfo &&
        (homeData as any).crawlSpaceInfo[claimedByField] !== undefined
    ) {
        value = (homeData as any).crawlSpaceInfo[claimedByField]
    }
    // Check in miscellaneousInfo
    else if (
        (homeData as any).miscellaneousInfo &&
        (homeData as any).miscellaneousInfo[claimedByField] !== undefined
    ) {
        value = (homeData as any).miscellaneousInfo[claimedByField]
    }
    // Check in roofingInfo
    else if (
        homeData.roofingInfo &&
        homeData.roofingInfo[
            claimedByField as keyof typeof homeData.roofingInfo
        ] !== undefined
    ) {
        value =
            homeData.roofingInfo[
                claimedByField as keyof typeof homeData.roofingInfo
            ]
    }
    // Check in cabinetInstallInfo
    else if (
        homeData.cabinetInstallInfo &&
        homeData.cabinetInstallInfo[
            claimedByField as keyof typeof homeData.cabinetInstallInfo
        ] !== undefined
    ) {
        value =
            homeData.cabinetInstallInfo[
                claimedByField as keyof typeof homeData.cabinetInstallInfo
            ]
    }
    // Check in trimInstallInfo
    else if (
        homeData.trimInstallInfo &&
        homeData.trimInstallInfo[
            claimedByField as keyof typeof homeData.trimInstallInfo
        ] !== undefined
    ) {
        value =
            homeData.trimInstallInfo[
                claimedByField as keyof typeof homeData.trimInstallInfo
            ]
    }
    // Check in homeStatusInfo
    else if (
        homeData.homeStatusInfo &&
        homeData.homeStatusInfo[
            claimedByField as keyof typeof homeData.homeStatusInfo
        ] !== undefined
    ) {
        value =
            homeData.homeStatusInfo[
                claimedByField as keyof typeof homeData.homeStatusInfo
            ]
    }

    // If value is not found, return empty string
    if (value === null || value === undefined) {
        return ""
    }

    // Return the value as is (it will be string)
    return value
}

export function WorkflowFlowchart({ homeId }: WorkflowFlowchartProps) {
    const [homeData, setHomeData] = useState<Home | null>(null)
    const [loading, setLoading] = useState(true)
    const [selectedStepIndex, setSelectedStepIndex] = useState<number | null>(
        null
    )
    const [selectedNodeLabel, setSelectedNodeLabel] = useState<string>("")
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [formData, setFormData] = useState<{
        status: StatusOption | ""
        claimedBy: OrganizationOption | ""
        workOrder: number | ""
    }>({
        status: "",
        claimedBy: "",
        workOrder: "",
    })
    const [isSaving, setIsSaving] = useState(false)
    const [windowWidth, setWindowWidth] = useState(1326)
    const [nodes, setNodes] = useState<any[]>([])
    const [partners, setPartners] = useState<Partner[]>([])
    const [activeWorkOrders, setActiveWorkOrders] = useState<
        Array<{ id: number; orderNumber: string; createdAt: string }>
    >([])

    // Get current logged-in user information
    const { session, profile, isLoading: sessionLoading } = useSession()

    // Console log work orders where current user is assigned as team lead
    useEffect(() => {
        const fetchTeamLeadWorkOrders = async () => {
            if (session && profile && homeData) {
                try {
                    // Fetch all work orders
                    const workOrdersResponse = await getAllWorkOrders({
                        page: 1,
                        limit: 1000, // Get a large number to ensure we get all work orders
                        sortBy: "createdAt",
                        sortOrder: "desc",
                    })

                    if (
                        workOrdersResponse.success &&
                        workOrdersResponse.data?.workOrders
                    ) {
                        // Check if user is Admin or BaseLeader
                        const isAdminOrBaseLeader =
                            session.role === UserRole.ADMIN ||
                            session.role === UserRole.BASE_LEADER

                        let filteredWorkOrders

                        if (isAdminOrBaseLeader) {
                            // For Admin and BaseLeader: show all active work orders for this home
                            filteredWorkOrders =
                                workOrdersResponse.data.workOrders.filter(
                                    (workOrder: any) => {
                                        // Handle potential data type mismatches by converting both to numbers
                                        const workOrderHomeId = Number(
                                            workOrder.home?.id
                                        )
                                        const currentHomeId = Number(
                                            homeData?.id
                                        )
                                        const homeMatch =
                                            workOrderHomeId === currentHomeId
                                        return homeMatch
                                    }
                                )
                        } else {
                            // For other roles: filter work orders where current user is the team lead AND matches current home ID
                            filteredWorkOrders =
                                workOrdersResponse.data.workOrders.filter(
                                    (workOrder: any) => {
                                        // Handle potential data type mismatches by converting both to numbers
                                        const workOrderTeamLeadId = Number(
                                            workOrder.teamLead?.id
                                        )
                                        const workOrderHomeId = Number(
                                            workOrder.home?.id
                                        )
                                        const currentProfileId = Number(
                                            profile.id
                                        )
                                        const currentHomeId = Number(
                                            homeData?.id
                                        )

                                        const teamLeadMatch =
                                            workOrderTeamLeadId ===
                                            currentProfileId
                                        const homeMatch =
                                            workOrderHomeId === currentHomeId

                                        return teamLeadMatch && homeMatch
                                    }
                                )
                        }

                        // Filter work orders with "Active" status for the dropdown

                        const activeWorkOrders = filteredWorkOrders
                            .filter((workOrder: any) => {
                                const isActive = workOrder.status === "Active"
                                return isActive
                            })
                            .map((workOrder: any) => ({
                                id: workOrder.id,
                                orderNumber: workOrder.orderNumber,
                                createdAt: workOrder.createdAt,
                            }))
                            .sort(
                                (
                                    a: { createdAt: string },
                                    b: { createdAt: string }
                                ) =>
                                    new Date(b.createdAt).getTime() -
                                    new Date(a.createdAt).getTime()
                            ) // Sort by most recent first

                        setActiveWorkOrders(activeWorkOrders)
                    }
                } catch (error) {
                    // Error fetching work orders - silently handle
                }
            }
        }

        fetchTeamLeadWorkOrders()
    }, [session, profile, homeData?.id, homeData])

    // Custom component to handle responsive fitView
    const ResponsiveFitView = () => {
        const { fitView } = useReactFlow()

        useEffect(() => {
            const handleResize = () => {
                const currentWidth = window.innerWidth
                if (currentWidth <= 575) {
                    // Trigger fitView when screen width is 575px or less
                    fitView({
                        padding: 0,
                        includeHiddenNodes: false,
                        minZoom: 0.8,
                        maxZoom: 1.2,
                        duration: 800,
                    })
                }
                // Additional trigger at 450px for better fit on smaller screens
                if (currentWidth <= 450) {
                    fitView({
                        padding: 0,
                        includeHiddenNodes: false,
                        minZoom: 0.7,
                        maxZoom: 1.3,
                        duration: 800,
                    })
                }
                // Final trigger at 400px for very small screens (400px down to 375px)
                if (currentWidth <= 400) {
                    fitView({
                        padding: 0,
                        includeHiddenNodes: false,
                        minZoom: 0.6,
                        maxZoom: 1.4,
                        duration: 800,
                    })
                }
            }

            // Initial check
            handleResize()

            // Add event listener
            window.addEventListener("resize", handleResize)

            return () => {
                window.removeEventListener("resize", handleResize)
            }
        }, [fitView])

        return null // This component doesn't render anything
    }

    useEffect(() => {
        const fetchHomeData = async () => {
            try {
                // Fetch home data from server
                const homeResponse = await getHome(homeId)
                if (homeResponse.success) {
                    console.log("Complete Home Data:", homeResponse.data)
                    setHomeData(homeResponse.data)
                } else {
                    // Fallback to mock data if API fails
                    setHomeData(
                        createMockHomeFromData(mockHomeData, homeId)
                    )
                }

                // Using mock data for testing - modify values in mockHomeData above to test different scenarios
                // setHomeData(
                //     createMockHomeFromData(mockHomeData, parseInt(homeId))
                // )
            } catch (error) {
                // Handle error silently or show user-friendly message
            } finally {
                setLoading(false)
            }
        }

        fetchHomeData()
    }, [homeId])

    // Fetch partners data
    useEffect(() => {
        const fetchPartners = async () => {
            try {
                const res = await authFetchJson<Partner[]>("/organizations")
                if (res.success && res.data) {
                    // Filter only active partners
                    const activePartners = res.data.filter(
                        (partner: Partner) => partner.isActive
                    )
                    setPartners(activePartners)
                }
            } catch (error) {
                // Handle error silently or show user-friendly message
            }
        }

        fetchPartners()
    }, [])

    // Helper function to get field value from home data based on field name
    const getFieldValueFromHomeData = (homeData: Home, field: string): any => {
        // Check in repairabilityInfo
        if (
            homeData.repairabilityInfo &&
            homeData.repairabilityInfo[
                field as keyof typeof homeData.repairabilityInfo
            ] !== undefined
        ) {
            return homeData.repairabilityInfo[
                field as keyof typeof homeData.repairabilityInfo
            ]
        }
        // Check in treeDebrisInfo
        else if (
            homeData.treeDebrisInfo &&
            homeData.treeDebrisInfo[
                field as keyof typeof homeData.treeDebrisInfo
            ] !== undefined
        ) {
            return homeData.treeDebrisInfo[
                field as keyof typeof homeData.treeDebrisInfo
            ]
        }
        // Check in crawlSpaceInfo
        else if (
            (homeData as any).crawlSpaceInfo &&
            (homeData as any).crawlSpaceInfo[field] !== undefined
        ) {
            return (homeData as any).crawlSpaceInfo[field]
        }
        // Check in miscellaneousInfo
        else if (
            (homeData as any).miscellaneousInfo &&
            (homeData as any).miscellaneousInfo[field] !== undefined
        ) {
            return (homeData as any).miscellaneousInfo[field]
        }
        // Check in permitInspectionStatusInfo
        else if (
            (homeData as any).permitInspectionStatusInfo &&
            (homeData as any).permitInspectionStatusInfo[field] !== undefined
        ) {
            return (homeData as any).permitInspectionStatusInfo[field]
        }
        // Check in muckGutInfo
        else if (
            homeData.muckGutInfo &&
            homeData.muckGutInfo[field as keyof typeof homeData.muckGutInfo] !==
                undefined
        ) {
            return homeData.muckGutInfo[
                field as keyof typeof homeData.muckGutInfo
            ]
        }
        // Special case for muckGutInfo claimedBy field
        else if (
            field === "claimedBy" &&
            homeData.muckGutInfo &&
            (homeData.muckGutInfo as any)["claimedBy"] !== undefined
        ) {
            return (homeData.muckGutInfo as any)["claimedBy"]
        }
        // Check in moldRemediationInfo
        else if (
            homeData.moldRemediationInfo &&
            homeData.moldRemediationInfo[
                field as keyof typeof homeData.moldRemediationInfo
            ] !== undefined
        ) {
            return homeData.moldRemediationInfo[
                field as keyof typeof homeData.moldRemediationInfo
            ]
        }
        // Check in roofingInfo
        else if (
            homeData.roofingInfo &&
            homeData.roofingInfo[field as keyof typeof homeData.roofingInfo] !==
                undefined
        ) {
            return homeData.roofingInfo[
                field as keyof typeof homeData.roofingInfo
            ]
        }
        // Check in rebuildPrepInfo
        else if (
            homeData.rebuildPrepInfo &&
            homeData.rebuildPrepInfo[
                field as keyof typeof homeData.rebuildPrepInfo
            ] !== undefined
        ) {
            return homeData.rebuildPrepInfo[
                field as keyof typeof homeData.rebuildPrepInfo
            ]
        }
        // Check in roughCarpentryInfo
        else if (
            homeData.roughCarpentryInfo &&
            homeData.roughCarpentryInfo[
                field as keyof typeof homeData.roughCarpentryInfo
            ] !== undefined
        ) {
            return homeData.roughCarpentryInfo[
                field as keyof typeof homeData.roughCarpentryInfo
            ]
        }
        // Check in hvacInfo
        else if (
            homeData.hvacInfo &&
            homeData.hvacInfo[field as keyof typeof homeData.hvacInfo] !==
                undefined
        ) {
            return homeData.hvacInfo[field as keyof typeof homeData.hvacInfo]
        }
        // Check in electricalInfo
        else if (
            homeData.electricalInfo &&
            homeData.electricalInfo[
                field as keyof typeof homeData.electricalInfo
            ] !== undefined
        ) {
            return homeData.electricalInfo[
                field as keyof typeof homeData.electricalInfo
            ]
        }
        // Check in plumbingInfo
        else if (
            homeData.plumbingInfo &&
            homeData.plumbingInfo[
                field as keyof typeof homeData.plumbingInfo
            ] !== undefined
        ) {
            return homeData.plumbingInfo[
                field as keyof typeof homeData.plumbingInfo
            ]
        }
        // Check in insulationInstallInfo
        else if (
            homeData.insulationInstallInfo &&
            homeData.insulationInstallInfo[
                field as keyof typeof homeData.insulationInstallInfo
            ] !== undefined
        ) {
            return homeData.insulationInstallInfo[
                field as keyof typeof homeData.insulationInstallInfo
            ]
        }
        // Check in drywallHangingInfo
        else if (
            homeData.drywallHangingInfo &&
            homeData.drywallHangingInfo[
                field as keyof typeof homeData.drywallHangingInfo
            ] !== undefined
        ) {
            return homeData.drywallHangingInfo[
                field as keyof typeof homeData.drywallHangingInfo
            ]
        }
        // Check in mudAndTapeInfo
        else if (
            homeData.mudAndTapeInfo &&
            homeData.mudAndTapeInfo[
                field as keyof typeof homeData.mudAndTapeInfo
            ] !== undefined
        ) {
            return homeData.mudAndTapeInfo[
                field as keyof typeof homeData.mudAndTapeInfo
            ]
        }
        // Check in interiorPaintInfo
        else if (
            homeData.interiorPaintInfo &&
            homeData.interiorPaintInfo[
                field as keyof typeof homeData.interiorPaintInfo
            ] !== undefined
        ) {
            return homeData.interiorPaintInfo[
                field as keyof typeof homeData.interiorPaintInfo
            ]
        }
        // Check in cabinetInstallInfo
        else if (
            homeData.cabinetInstallInfo &&
            homeData.cabinetInstallInfo[
                field as keyof typeof homeData.cabinetInstallInfo
            ] !== undefined
        ) {
            return homeData.cabinetInstallInfo[
                field as keyof typeof homeData.cabinetInstallInfo
            ]
        }
        // Check in flooringInstallInfo
        else if (
            homeData.flooringInstallInfo &&
            homeData.flooringInstallInfo[
                field as keyof typeof homeData.flooringInstallInfo
            ] !== undefined
        ) {
            return homeData.flooringInstallInfo[
                field as keyof typeof homeData.flooringInstallInfo
            ]
        }
        // Check in installInteriorDoorsInfo
        else if (
            homeData.installInteriorDoorsInfo &&
            homeData.installInteriorDoorsInfo[
                field as keyof typeof homeData.installInteriorDoorsInfo
            ] !== undefined
        ) {
            return homeData.installInteriorDoorsInfo[
                field as keyof typeof homeData.installInteriorDoorsInfo
            ]
        }
        // Check in exteriorDoorInstallInfo
        else if (
            homeData.exteriorDoorInstallInfo &&
            homeData.exteriorDoorInstallInfo[
                field as keyof typeof homeData.exteriorDoorInstallInfo
            ] !== undefined
        ) {
            return homeData.exteriorDoorInstallInfo[
                field as keyof typeof homeData.exteriorDoorInstallInfo
            ]
        }
        // Check in trimInstallInfo
        else if (
            homeData.trimInstallInfo &&
            homeData.trimInstallInfo[
                field as keyof typeof homeData.trimInstallInfo
            ] !== undefined
        ) {
            return homeData.trimInstallInfo[
                field as keyof typeof homeData.trimInstallInfo
            ]
        }
        // Check in bathroomRebuildInfo
        else if (
            (homeData as any).bathroomRebuildInfo &&
            (homeData as any).bathroomRebuildInfo[field] !== undefined
        ) {
            return (homeData as any).bathroomRebuildInfo[field]
        }
        // Check in homeStatusInfo
        else if (
            homeData.homeStatusInfo &&
            homeData.homeStatusInfo[
                field as keyof typeof homeData.homeStatusInfo
            ] !== undefined
        ) {
            return homeData.homeStatusInfo[
                field as keyof typeof homeData.homeStatusInfo
            ]
        }

        return undefined
    }

    const handleSave = async () => {
        if (
            selectedStepIndex === null ||
            selectedStepIndex === undefined ||
            !homeData
        )
            return

        setIsSaving(true)

        try {
            // Get the node label for the selected step
            const nodeLabels = [
                "Assess Needs",
                "M&G Crawl Space",
                "Muck and Gut Interior",
                "Tarp Roof",
                "Tree Work",
                "Insulate Crawl Space",
                "Mold Remediation",
                "Install Roofing",
                "Repair Driveway",
                "Misc",
                "Rebuild Prep",
                "Roof Final Inspection",
                "Rough Carpentry",
                "Rough Electrical",
                "Rough Mechanical",
                "Rough Plumbing",
                "Rough Carpentry Inspection",
                "Rough Electrical Inspection",
                "Rough Mechanical Inspection",
                "Rough Plumbing / Tub Set Inspection",
                "Insulation",
                "Insulation Inspection",
                "Drywall Hanging",
                "Drywall Inspection",
                "Mud and Tape",
                "Interior Paint",
                "Install Cabinets",
                "HVAC Final",
                "Bathroom Rebuild",
                "Install Counters",
                "Electrical Fixtures",
                "Plumbing Fixtures",
                "Final Electrical Inspection",
                "Final Plumbing Inspection",
                "Final HVAC Inspection",
                "Final Inspection",
                "Functionally Complete",
                "Flooring Install",
                "Install Interior Doors",
                "Install Exterior Doors",
                "Trim Install",
                "Complete",
                "Heavy Equipment / Demo",
                "Exterior Debris Removal",
                "Install Appliances",
            ]

            const selectedNodeLabel = nodeLabels[selectedStepIndex]
            const stepField = nodeFieldMapping[selectedNodeLabel]?.field

            if (!stepField) {
                console.error(
                    "No field mapping found for node:",
                    selectedNodeLabel
                )
                return
            }

            // Check if this is an inspection node (skip claimed by updates for these)
            const inspectionNodes = [
                "Roof Final Inspection",
                "Rough Carpentry Inspection", 
                "Rough Electrical Inspection",
                "Rough Mechanical Inspection",
                "Rough Plumbing / Tub Set Inspection",
                "Insulation Inspection",
                "Drywall Inspection",
                "Final Electrical Inspection",
                "Final Plumbing Inspection", 
                "Final HVAC Inspection",
                "Final Inspection"
            ]
            
            const isInspectionNode = inspectionNodes.includes(selectedNodeLabel)
            
            // Call the API to update the work scope status
            const response = await updateWorkScopeStatus(
                homeData.id,
                stepField,
                formData.status,
                isInspectionNode ? "" : formData.claimedBy  // Skip claimed by for inspection nodes
            )

            if (response.success) {
                // TEMPORARY WORKAROUND: Force multiple refreshes due to backend caching issues
                let attempts = 0
                let homeResponse = null

                while (attempts < 3) {
                    // Add a delay before refreshing to ensure the backend has processed the update
                    await new Promise((resolve) =>
                        setTimeout(resolve, 1000 * (attempts + 1))
                    )

                    // Refresh the home data to get updated status
                    homeResponse = await getHome(homeData.id)

                    if (homeResponse.success) {
                        // Check if the data is actually updated
                        const updatedValue = getFieldValueFromHomeData(
                            homeResponse.data,
                            stepField
                        )

                        if (updatedValue === formData.status) {
                            break
                        }
                    }

                    attempts++
                }

                if (homeResponse?.success) {
                    const updatedValue = getFieldValueFromHomeData(
                        homeResponse.data,
                        stepField
                    )

                    // Use the correct claimedBy field name based on the step field
                    const claimedByFieldMapping: Record<string, string> = {
                        roofTarpNeeded: "roofTarpClaimedBy",
                        roofing: "roofingClaimedBy",
                        treeWorkNeeded: "treeClaimedBy",
                        exteriorDebrisRemovalNeeded: "debrisClaimedBy",
                        heavyEquipmentNeeded: "heavyClaimedBy",
                        muckGutNeeded: "claimedBy",
                        moldRemediation: "claimedBy",
                        rebuildPrep: "claimedBy",
                        roughCarpentry: "claimedBy",
                        electricalRoughIn: "electricalRoughInClaimedBy",
                        electrical: "electricalClaimedBy",
                        mechanicalRoughIn: "mechanicalRoughInClaimedBy",
                        hvac: "hvacClaimedBy",
                        plumbingRoughIn: "plumbingRoughInClaimedBy",
                        plumbing: "plumbingClaimedBy",
                        crawlSpaceMuck: "crawlSpaceMuckClaimedBy",
                        crawlSpaceInsulation: "crawlSpaceInsulationClaimedBy",
                        insulationInstall: "claimedBy",
                        drywallHanging: "drywallHangingClaimedBy",
                        mudAndTape: "mudAndTapeClaimedBy",
                        interiorPaint: "interiorPaintClaimedBy",
                        cabinetInstall: "cabinetClaimedBy",
                        counterInstall: "counterClaimedBy",
                        applianceInstall: "applianceClaimedBy",
                        installInteriorDoors: "claimedBy",
                        trimInstall: "claimedBy",
                        flooringInstall: "claimedBy",
                        installExteriorDoors: "claimedBy",
                        miscellaneous: "claimedBy",
                        drivewayRepair: "drivewayClaimedBy",
                        assessmentCompleted: "assessmentCompletedClaimedBy",
                    }

                    const correctClaimedByField =
                        claimedByFieldMapping[stepField] ||
                        `${stepField}ClaimedBy`
                    const updatedClaimedBy = getFieldValueFromHomeData(
                        homeResponse.data,
                        correctClaimedByField
                    )

                    setHomeData(homeResponse.data)
                }

                setIsModalOpen(false)
                setSelectedStepIndex(null)
                setSelectedNodeLabel("")
                setFormData({ status: "", claimedBy: "", workOrder: "" })
            } else {
                // You might want to show an error toast here
            }
        } catch (error) {
            // You might want to show an error toast here
        } finally {
            setIsSaving(false)
        }
    }

    const handleCancel = () => {
        setIsModalOpen(false)
        setSelectedStepIndex(null)
        setSelectedNodeLabel("")
        setFormData({ status: "", claimedBy: "", workOrder: "" })
    }

    ///////////////////////////////// ReatFlow flowchart solution ///////////////////

    // Handle window resize for flowchart responsive design
    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth)
        }

        // Set initial width
        setWindowWidth(window.innerWidth)

        window.addEventListener("resize", handleResize)
        return () => window.removeEventListener("resize", handleResize)
    }, [])

    const CustomNode = ({ data, id }: any) => {
        // Find edges where this node is involved
        const sourceEdges = initialEdges.filter((edge) => edge.source === id)
        const targetEdges = initialEdges.filter((edge) => edge.target === id)

        // Check for specific connections that need side handles
        const hasRightConnection = sourceEdges.some(
            (edge) => edge.sourceHandle === "right"
        )
        const hasLeftConnection = targetEdges.some(
            (edge) => edge.targetHandle === "left"
        )

        // Check for connections that don't use side handles
        const hasRegularSourceConnection = sourceEdges.some(
            (edge) => !edge.sourceHandle || edge.sourceHandle !== "right"
        )
        const hasRegularTargetConnection = targetEdges.some(
            (edge) => !edge.targetHandle || edge.targetHandle !== "left"
        )

        // Get the status color for this node
        const statusColor = data.statusColor || "bg-gray-200"

        // Check if this node needs a progress border
        const needsProgressBorder = [
            "25%",
            "50%",
            "75%",
            "Ready for Inspection",
        ].includes(data.status)

        // Check if this node needs a progress label (including "Needed Not Started" and "Reassessment Needed")
        const needsProgressLabel = [
            "Needed Not Started",
            "Reassessment Needed",
            "25%",
            "50%",
            "75%",
            "Ready for Inspection",
        ].includes(data.status)

        const progressPercentage =
            data.status === "Ready for Inspection" ? 100 : parseInt(data.status)
        const progressLabel =
            data.status === "Ready for Inspection"
                ? "100%"
                : data.status === "Needed Not Started" || data.status === "Reassessment Needed"
                ? "0%"
                : data.status

        return (
            <div className="relative">
                {/* Main node with progress border */}
                {needsProgressBorder ? (
                    <div
                        className="rounded-lg p-[6px]"
                        style={{
                            background: `conic-gradient(from 0deg, #a3e635 0deg, #a3e635 ${
                                progressPercentage * 3.6
                            }deg, #ffffff ${
                                progressPercentage * 3.6
                            }deg, #ffffff 360deg)`,
                        }}
                    >
                        <div
                            className={`${statusColor} p-[10px] ${
                                data.label === "Functionally Complete" ||
                                data.label === "Complete"
                                    ? ""
                                    : "cursor-pointer"
                            } h-[60px] w-[120px] flex justify-center items-center text-xs font-semibold rounded-lg shadow-md transition-transform hover:scale-105 text-white text-center leading-none relative`}
                        >
                            {hasRegularTargetConnection && (
                                <Handle
                                    type="target"
                                    position={Position.Top}
                                    style={{ background: "white" }}
                                />
                            )}
                            {hasRegularSourceConnection && (
                                <Handle
                                    type="source"
                                    position={Position.Bottom}
                                    style={{ background: "white" }}
                                />
                            )}
                            {hasRightConnection && (
                                <Handle
                                    type="source"
                                    position={Position.Right}
                                    id="right"
                                    style={{ background: "white" }}
                                />
                            )}
                            {hasLeftConnection && (
                                <Handle
                                    type="target"
                                    position={Position.Left}
                                    id="left"
                                    style={{ background: "white" }}
                                />
                            )}
                            <span className="text-center leading-normal">
                                {data.label}
                            </span>

                            {/* Progress label */}
                            <div className="absolute -top-4 -right-2 bg-lime-400 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[22px] text-center z-10">
                                {progressLabel}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div
                        className={`${statusColor} p-[10px] ${
                            data.label === "Functionally Complete" ||
                            data.label === "Complete"
                                ? ""
                                : "cursor-pointer"
                        } h-[60px] w-[120px] flex justify-center items-center text-xs font-semibold rounded-lg shadow-md transition-transform hover:scale-105 text-white text-center leading-none relative border-6 border-white`}
                    >
                        {hasRegularTargetConnection && (
                            <Handle
                                type="target"
                                position={Position.Top}
                                style={{ background: "white" }}
                            />
                        )}
                        {hasRegularSourceConnection && (
                            <Handle
                                type="source"
                                position={Position.Bottom}
                                style={{ background: "white" }}
                            />
                        )}
                        {hasRightConnection && (
                            <Handle
                                type="source"
                                position={Position.Right}
                                id="right"
                                style={{ background: "white" }}
                            />
                        )}
                        {hasLeftConnection && (
                            <Handle
                                type="target"
                                position={Position.Left}
                                id="left"
                                style={{ background: "white" }}
                            />
                        )}
                        <span className="text-center leading-normal">
                            {data.label}
                        </span>

                        {/* Progress label for "Needed Not Started" */}
                        {needsProgressLabel && !needsProgressBorder && (
                            <div className="absolute -top-4 -right-2 bg-lime-400 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[22px] text-center z-10">
                                {progressLabel}
                            </div>
                        )}
                    </div>
                )}
            </div>
        )
    }

    // Responsive node positioning function
    // Memoize responsive positions to prevent infinite loops
    const responsivePositions = useMemo(() => {
        // Base positions for desktop (1326px+)
        const basePositions = [
            { x: 380, y: 100 }, // 0 - Assess Needs
            { x: 200, y: 200 }, // 1 - Assess Needs
            { x: 380, y: 200 }, // 2 - Muck & Gut
            { x: 560, y: 200 }, // 3 - Trap Floor
            { x: 290, y: 300 }, // 4 - Tree Work
            { x: 200, y: 400 }, // 5 - Exterior Debris Removal
            { x: 380, y: 400 }, // 6 - Mold Remediation
            { x: 560, y: 400 }, // 7 - Install Roofing
            { x: 290, y: 500 }, // 8 - Heavy Equipment / Demo
            { x: 200, y: 600 }, // 9 - Misc
            { x: 380, y: 600 }, // 10 - Rebuild Prep
            { x: 560, y: 600 }, // 11 - Roof Final Inspection
            { x: 200, y: 800 }, // 12 - Rough Carpentry
            { x: 380, y: 800 }, // 13 - Rough Electrical
            { x: 290, y: 900 }, // 14 - Rough Mechanical
            { x: 470, y: 900 }, // 15 - Rough Plumbing
            { x: 200, y: 1000 }, // 16 - Rough Carpentry Inspection
            { x: 380, y: 1000 }, // 17 - Rough Electrical Inspection
            { x: 290, y: 1100 }, // 18 - Rough Mechanical Inspection
            { x: 470, y: 1100 }, // 19 - Rough Plumbing / Tub Set Inspection
            { x: 380, y: 1400 }, // 20 - Insulation
            { x: 380, y: 1500 }, // 21 - Insulation Inspection
            { x: 380, y: 1600 }, // 22 - Drywall Hanging
            { x: 380, y: 1700 }, // 23 - Drywall Inspection
            { x: 380, y: 1800 }, // 24 - Mud and Tape
            { x: 200, y: 2000 }, // 25 - Interior Paint
            { x: 380, y: 2000 }, // 26 - Install Cabinets
            { x: 560, y: 2000 }, // 27 - HVAC Final
            { x: 290, y: 2100 }, // 28 - Bathroom Rebuild
            { x: 380, y: 2200 }, // 29 - Install Counters
            { x: 470, y: 2300 }, // 30 - Electrical Fixtures
            { x: 200, y: 2500 }, // 31 - Plumbing Fixtures
            { x: 380, y: 2600 }, // 32 - Final Electrical Inspection
            { x: 200, y: 2600 }, // 33 - Final Plumbing Inspection
            { x: 560, y: 2600 }, // 34 - Final HVAC Inspection
            { x: 380, y: 2800 }, // 35 - Final Inspection
            { x: 380, y: 2900 }, // 36 - Functionally Complete
            { x: 200, y: 3100 }, // 37 - Flooring Install
            { x: 380, y: 3100 }, // 38 - Install Interior Doors
            { x: 560, y: 3100 }, // 39 - Install Exterior Doors
            { x: 380, y: 3300 }, // 40 - Trim Install
            { x: 380, y: 3400 }, // 41 - Complete
            { x: 470, y: 300 }, // 42 - Heavy Equipment / Demo
            { x: 470, y: 500 }, // 43 - Exterior Debris Removal
            { x: 380, y: 2400 }, // 44 - Install Appliances
        ]

        // Get current viewport width
        const viewportWidth = windowWidth

        // Responsive scaling factors
        const scaleX = 1
        const scaleY = 1
        let offsetX = 0

        if (viewportWidth < 640) {
            offsetX = -120
        } else if (viewportWidth < 768) {
            offsetX = -80
        } else if (viewportWidth < 800) {
            offsetX = -120
        } else if (viewportWidth < 900) {
            offsetX = -100
        } else if (viewportWidth < 990) {
            offsetX = -50
        } else if (viewportWidth < 1024) {
            offsetX = 0
        } else if (viewportWidth < 1150) {
            offsetX = 30
        } else if (viewportWidth < 1326) {
            offsetX = 80
        } else {
            offsetX = 150
        }

        return basePositions.map((pos) => ({
            x: pos.x * scaleX + offsetX,
            y: pos.y * scaleY,
        }))
    }, [windowWidth])

    // Create nodes with status colors
    const createNodesWithStatus = useCallback(() => {
        const nodeLabels = [
            "Assess Needs",
            "M&G Crawl Space",
            "Muck and Gut Interior",
            "Tarp Roof",
            "Tree Work",
            "Insulate Crawl Space",
            "Mold Remediation",
            "Install Roofing",
            "Repair Driveway",
            "Misc",
            "Rebuild Prep",
            "Roof Final Inspection",
            "Rough Carpentry",
            "Rough Electrical",
            "Rough Mechanical",
            "Rough Plumbing",
            "Rough Carpentry Inspection",
            "Rough Electrical Inspection",
            "Rough Mechanical Inspection",
            "Rough Plumbing / Tub Set Inspection",
            "Insulation",
            "Insulation Inspection",
            "Drywall Hanging",
            "Drywall Inspection",
            "Mud and Tape",
            "Interior Paint",
            "Install Cabinets",
            "HVAC Final",
            "Bathroom Rebuild",
            "Install Counters",
            "Electrical Fixtures",
            "Plumbing Fixtures",
            "Final Electrical Inspection",
            "Final Plumbing Inspection",
            "Final HVAC Inspection",
            "Final Inspection",
            "Functionally Complete",
            "Flooring Install",
            "Install Interior Doors",
            "Install Exterior Doors",
            "Trim Install",
            "Complete",
            "Heavy Equipment / Demo",
            "Exterior Debris Removal",
            "Install Appliances",
        ]

        return nodeLabels.map((label, index) => {
            // Special logic for "Functionally Complete" node
            if (label === "Functionally Complete" && homeData) {
                // Get all previous nodes' statuses (nodes before "Functionally Complete")
                const previousNodeStatuses = nodeLabels
                    .slice(0, index) // Get all nodes before this one
                    .map((nodeLabel) =>
                        determineWorkStatus(homeData, nodeLabel)
                    )

                // Check if all previous nodes are either "No Assistance Required", "Complete" or null or undefined
                const allNodesReady = previousNodeStatuses.every(
                    (status) =>
                        status === "No Assistance Required" ||
                        status === "Complete" ||
                        status === null ||
                        status === undefined
                )

                // Set status color based on the condition
                const statusColor = allNodesReady
                    ? "bg-green-500"
                    : "bg-yellow-200"
                const status = allNodesReady ? "Complete" : "Not Complete"

                return {
                    id: (index + 1).toString(),
                    type: "custom",
                    position: responsivePositions[index],
                    data: {
                        label,
                        statusColor,
                        status,
                        claimedBy: "", // Functionally Complete doesn't have a claimedBy
                        stepIndex: index,
                    },
                }
            }

            // Special logic for "Complete" node
            if (label === "Complete" && homeData) {
                // Get all previous nodes' statuses (nodes before "Complete"), excluding "Functionally Complete"
                const previousNodeStatuses = nodeLabels
                    .slice(0, index) // Get all nodes before this one
                    .filter(
                        (nodeLabel) => nodeLabel !== "Functionally Complete"
                    ) // Exclude "Functionally Complete"
                    .map((nodeLabel) =>
                        determineWorkStatus(homeData, nodeLabel)
                    )

                // Check if all previous nodes are either "No Assistance Required", "Complete" or null or undefined
                const allNodesReady = previousNodeStatuses.every(
                    (status) =>
                        status === "No Assistance Required" ||
                        status === "Complete" ||
                        status === null ||
                        status === undefined
                )

                // Set status color based on the condition
                const statusColor = allNodesReady
                    ? "bg-green-500"
                    : "bg-yellow-200"
                const status = allNodesReady ? "Complete" : "Not Complete"

                return {
                    id: (index + 1).toString(),
                    type: "custom",
                    position: responsivePositions[index],
                    data: {
                        label,
                        statusColor,
                        status,
                        claimedBy: "", // Complete doesn't have a claimedBy
                        stepIndex: index,
                    },
                }
            }

            // Determine the status for this node
            let status: string = "Unknown"
            let claimedBy: string = ""
            if (homeData) {
                status = determineWorkStatus(homeData, label)
                claimedBy = determineClaimedBy(homeData, label)
            }

            // For Assess Needs, always treat as AR Scopes for color purposes
            const claimedByForColor =
                label === "Assess Needs" ? "AR Scopes" : claimedBy

            // Get the status color
            const statusColor = getStatusColor(status, claimedByForColor)

            return {
                id: (index + 1).toString(),
                type: "custom",
                position: responsivePositions[index],
                data: {
                    label,
                    statusColor,
                    status,
                    claimedBy,
                    stepIndex: index,
                },
            }
        })
    }, [homeData, responsivePositions])

    // Update nodes when homeData changes or update nodes positions when window resizes (for responsive design)
    useEffect(() => {
        if (homeData) {
            const updatedNodes = createNodesWithStatus()
            setNodes(updatedNodes)
        }
    }, [homeData, windowWidth, createNodesWithStatus])

    const initialEdges: any[] = [
        {
            id: "c2-6",
            source: "2",
            target: "6",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c3-7",
            source: "3",
            target: "7",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c4-8",
            source: "4",
            target: "8",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c7-11",
            source: "7",
            target: "11",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c8-12",
            source: "8",
            target: "12",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c11-13",
            source: "11",
            target: "13",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c11-14",
            source: "11",
            target: "14",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c11-15",
            source: "11",
            target: "15",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c11-16",
            source: "11",
            target: "16",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c13-17",
            source: "13",
            target: "17",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c14-18",
            source: "14",
            target: "18",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c15-19",
            source: "15",
            target: "19",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c16-20",
            source: "16",
            target: "20",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c17-21",
            source: "17",
            target: "21",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c18-21",
            source: "18",
            target: "21",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c19-21",
            source: "19",
            target: "21",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c20-21",
            source: "20",
            target: "21",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c21-22",
            source: "21",
            target: "22",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c22-23",
            source: "22",
            target: "23",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c23-24",
            source: "23",
            target: "24",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c24-25",
            source: "24",
            target: "25",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c25-26",
            source: "25",
            target: "26",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c25-27",
            source: "25",
            target: "27",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c25-28",
            source: "25",
            target: "28",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c25-29",
            source: "25",
            target: "29",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c25-31",
            source: "25",
            target: "31",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c27-30",
            source: "27",
            target: "30",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c30-45",
            source: "30",
            target: "45",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c31-33",
            source: "31",
            target: "33",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c28-35",
            source: "28",
            target: "35",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c30-32",
            source: "30",
            target: "32",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c29-32",
            source: "29",
            target: "32",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c32-34",
            source: "32",
            target: "34",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c33-36",
            source: "33",
            target: "36",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c34-36",
            source: "34",
            target: "36",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c35-36",
            source: "35",
            target: "36",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c36-37",
            source: "36",
            target: "37",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c37-38",
            source: "37",
            target: "38",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c37-39",
            source: "37",
            target: "39",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c37-40",
            source: "37",
            target: "40",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c38-41",
            source: "38",
            target: "41",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c39-41",
            source: "39",
            target: "41",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c40-41",
            source: "40",
            target: "41",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
        {
            id: "c41-42",
            source: "41",
            target: "42",
            style: { stroke: "#80A1B6", strokeWidth: 2 },
        },
    ]

    const handleNodeClick = useCallback(
        (event: any, node: any) => {
            // Prevent modal opening for "Functionally Complete" and "Complete" nodes
            const nodeLabel = node.data?.label || ""
            if (
                nodeLabel === "Functionally Complete" ||
                nodeLabel === "Complete"
            ) {
                return
            }

            // Use the stepIndex from the node data if available, otherwise fall back to array index
            const stepIndex =
                node.data?.stepIndex !== undefined
                    ? node.data.stepIndex
                    : nodes.findIndex((n) => n.id === node.id)

            if (stepIndex !== -1) {
                setSelectedStepIndex(stepIndex)
                setSelectedNodeLabel(node.data?.label || "")

                // Set current values in form
                const currentStep = nodes[stepIndex]
                const nodeLabel = node.data?.label || ""

                // For Assess Needs, automatically set claimedBy to "AR Scopes"
                const claimedByValue =
                    nodeLabel === "Assess Needs"
                        ? "AR Scopes"
                        : currentStep.data?.claimedBy || ""

                setFormData({
                    status: currentStep.data?.status || "",
                    claimedBy: claimedByValue,
                    workOrder:
                        activeWorkOrders.length > 0
                            ? activeWorkOrders[0].id
                            : "",
                })

                setIsModalOpen(true)
            }
        },
        [nodes, activeWorkOrders]
    )

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-gray-500">
                    Loading flowchart data...
                </div>
            </div>
        )
    }

    return (
        <div className="space-y-5">
            <div className={FlowchartWrapper}>
                <ReactFlow
                    nodes={nodes}
                    edges={initialEdges}
                    nodeTypes={{ custom: CustomNode }}
                    onNodeClick={handleNodeClick}
                    nodesDraggable={false}
                    nodesConnectable={false}
                    elementsSelectable={false}
                    zoomOnScroll={false}
                    panOnDrag={false}
                    panOnScroll={false}
                    zoomOnPinch={false}
                    zoomOnDoubleClick={false}
                    preventScrolling={false}
                    fitView
                    fitViewOptions={{
                        padding: 0.06,
                        includeHiddenNodes: false,
                        minZoom: 1,
                        maxZoom: 1,
                    }}
                    proOptions={{ hideAttribution: true }}
                >
                    <Background className="bg-gray-50" />
                    <Controls showInteractive={false} />
                    <ResponsiveFitView />
                </ReactFlow>
            </div>

            {/* Status Update Modal */}
            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>
                            Update Status - {selectedNodeLabel}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="claimedBy">Claimed By</Label>
                            <Select
                                value={
                                    formData.claimedBy === "Homeowner Scopes"
                                        ? "Homeowner"
                                        : formData.claimedBy
                                }
                                onValueChange={(value) => {
                                    const newClaimedBy =
                                        value as OrganizationOption
                                    setFormData((prev) => ({
                                        ...prev,
                                        claimedBy: newClaimedBy,
                                        status: "",
                                        workOrder:
                                            newClaimedBy === "AR Scopes" &&
                                            activeWorkOrders.length > 0
                                                ? activeWorkOrders[0].id
                                                : "",
                                    }))
                                }}
                                disabled={selectedNodeLabel === "Assess Needs" || [
                                    "Roof Final Inspection",
                                    "Rough Carpentry Inspection", 
                                    "Rough Electrical Inspection",
                                    "Rough Mechanical Inspection",
                                    "Rough Plumbing / Tub Set Inspection",
                                    "Insulation Inspection",
                                    "Drywall Inspection",
                                    "Final Electrical Inspection",
                                    "Final Plumbing Inspection", 
                                    "Final HVAC Inspection",
                                    "Final Inspection"
                                ].includes(selectedNodeLabel)}
                            >
                                <SelectTrigger
                                    className={
                                        selectedNodeLabel === "Assess Needs" || [
                                            "Roof Final Inspection",
                                            "Rough Carpentry Inspection", 
                                            "Rough Electrical Inspection",
                                            "Rough Mechanical Inspection",
                                            "Rough Plumbing / Tub Set Inspection",
                                            "Insulation Inspection",
                                            "Drywall Inspection",
                                            "Final Electrical Inspection",
                                            "Final Plumbing Inspection", 
                                            "Final HVAC Inspection",
                                            "Final Inspection"
                                        ].includes(selectedNodeLabel)
                                            ? "opacity-50"
                                            : ""
                                    }
                                >
                                    <SelectValue placeholder="Select organization" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="AR Scopes">
                                        AR Scopes
                                    </SelectItem>
                                    <SelectItem value="Homeowner">
                                        Homeowner
                                    </SelectItem>
                                    <SelectItem value="Partner Scopes">
                                        Partner Scopes
                                    </SelectItem>
                                    {/* Old dynamic partner options - commented out
                                    <SelectItem value="Homeowner">
                                        Homeowner
                                    </SelectItem>
                                    {partners.map((partner) => (
                                        <SelectItem
                                            key={partner.id}
                                            value={partner.name}
                                        >
                                            {partner.name}
                                        </SelectItem>
                                    ))}
                                    */}
                                </SelectContent>
                            </Select>
                            {selectedNodeLabel === "Assess Needs" && (
                                <p className="text-sm text-muted-foreground">
                                    Assess Needs is automatically assigned to AR
                                    Scopes
                                </p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="status">Status</Label>
                            <Select
                                value={formData.status}
                                onValueChange={(value) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        status: value as StatusOption,
                                    }))
                                }
                                disabled={formData.claimedBy !== "AR Scopes"}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select status" />
                                </SelectTrigger>
                                <SelectContent>
                                    {selectedNodeLabel === "Assess Needs" ? (
                                        <>
                                            <SelectItem value="No Assistance Required">
                                                No Assistance Required
                                            </SelectItem>
                                            <SelectItem value="Needed Not Started">
                                                Needed Not Started
                                            </SelectItem>
                                            <SelectItem value="Reassessment Needed">
                                                Reassessment Needed
                                            </SelectItem>
                                            <SelectItem value="Complete">
                                                Complete
                                            </SelectItem>
                                        </>
                                    ) : (
                                        <>
                                            <SelectItem value="Unknown">
                                                Unknown
                                            </SelectItem>
                                            <SelectItem value="No Assistance Required">
                                                No Assistance Required
                                            </SelectItem>
                                            <SelectItem value="Not Ready for Work">
                                                Not Ready for Work
                                            </SelectItem>
                                            <SelectItem value="Needed Not Started">
                                                Needed Not Started
                                            </SelectItem>
                                            <SelectItem value="25%">
                                                25%
                                            </SelectItem>
                                            <SelectItem value="50%">
                                                50%
                                            </SelectItem>
                                            <SelectItem value="75%">
                                                75%
                                            </SelectItem>
                                            <SelectItem value="Ready for Inspection">
                                                Ready for Inspection
                                            </SelectItem>
                                            {(session?.role ===
                                                UserRole.ADMIN ||
                                                session?.role ===
                                                    UserRole.BASE_LEADER) && (
                                                <SelectItem value="Complete">
                                                    Complete
                                                </SelectItem>
                                            )}
                                        </>
                                    )}
                                </SelectContent>
                            </Select>
                            {formData.claimedBy !== "AR Scopes" &&
                                formData.claimedBy && (
                                    <p className="text-sm text-muted-foreground">
                                        You can&apos;t change status of
                                        Homeowner or Partner scopes
                                    </p>
                                )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="workOrder">Work Order</Label>
                            <Select
                                value={formData.workOrder.toString()}
                                onValueChange={(value) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        workOrder: value ? parseInt(value) : "",
                                    }))
                                }
                                disabled={formData.claimedBy !== "AR Scopes"}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select work order" />
                                </SelectTrigger>
                                <SelectContent>
                                    {activeWorkOrders.length > 0 ? (
                                        activeWorkOrders.map((workOrder) => (
                                            <SelectItem
                                                key={workOrder.id}
                                                value={workOrder.id.toString()}
                                            >
                                                {workOrder.orderNumber}
                                            </SelectItem>
                                        ))
                                    ) : (
                                        <div className="p-4 text-center text-sm text-muted-foreground">
                                            No work orders available
                                        </div>
                                    )}
                                </SelectContent>
                            </Select>
                            {formData.claimedBy !== "AR Scopes" &&
                                formData.claimedBy && (
                                    <p className="text-sm text-muted-foreground">
                                        You can&apos;t select work orders for
                                        Homeowner or Partner scopes
                                    </p>
                                )}
                        </div>

                        <div className="flex gap-2 pt-4">
                            <Button
                                variant="outline"
                                onClick={handleCancel}
                                disabled={isSaving}
                                className="flex-1"
                                showLoader={false}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSave}
                                disabled={isSaving}
                                className="flex-1"
                            >
                                {isSaving ? "Saving..." : "Save"}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Legend */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                    <span className="font-medium">Legend</span>
                    <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                        />
                    </svg>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-yellow-600 rounded"></div>
                        <span>Unknown</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-gray-300 rounded"></div>
                        <span>No Assistance Required</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-neutral-700 rounded"></div>
                        <span>Not Ready for Work</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-blue-950 rounded"></div>
                        <span>AR Scopes</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-fuchsia-700 rounded"></div>
                        <span>Homeowner</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-violet-700 rounded"></div>
                        <span>Partner Scopes</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-emerald-700 rounded"></div>
                        <span>Complete</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-yellow-200 rounded"></div>
                        <span>
                            Not Complete (Functionality & Complete)
                        </span>
                    </div>
                </div>
            </div>
        </div>
    )
}

/*---> Styles <---*/
const FlowchartWrapper = `
w-full
h-[2500px]
sm400:h-[2600px]
sm420:h-[2800px]
sm470:h-[3100px]
sm520:h-[3400px]
sm570:h-[3500px]
border 
border-gray-300 
rounded-lg 
shadow-sm 
bg-white 
relative
overflow-hidden
px-[10px]
py-[10px]
`
